import { useMemo, useState, useEffect } from "react";
import Pagination from "../../components/pagination";
import AppointmentTabs from "./components/AppointmentTabs";
import AppointmentTable from "./components/AppointmentTable";
import AppointmentUtils from "./components/AppointmentUtils";
import AppointmentDetailsModal from "./components/AppointmentDetailsModal";
import ScheduleAppointmentModal from "./components/ScheduleAppointmentModal";
import RescheduleAppointmentModal from "./components/RescheduleAppointmentModal";
import CancelAppointmentModal from "./components/CancelAppointmentModal";
import { TRescheduleAppointmentDetails } from "../../types";
import { useGetAppointments } from "../../apis/doctor";
import { useAppSelector } from "../../hooks/useAppSelector";
import { Flex, Img } from "@chakra-ui/react";
import NoItemsCard from "../patients/components/NoPatientsCard";
import { isFutureDay } from "../patients/utils";
import { useQueryClient } from "@tanstack/react-query";
import Button from "../../components/ui/button";
import { add } from "../../assets";
import CompleteAppointmentModal from "./components/CompleteAppointmentModal";

const Appointment = () => {
  const [active, setActive] = useState("upcoming");
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 5;
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const [appType, setAppType] = useState<{ category: string; mode: string }>({
    category: "All",
    mode: "All",
  });
  const [appointmentStatus, setAppointmentStatus] = useState("All");

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);

  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const [selectedAppointment, setSelectedAppointment] =
    useState<TRescheduleAppointmentDetails>(
      {} as TRescheduleAppointmentDetails
    );

  const { doctorInfo } = useAppSelector((state) => state.auth);
  const queryClient = useQueryClient();
  const doctorId = doctorInfo?._id;

  const [isMediumScreenOrUp, setIsMediumScreenOrUp] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      setIsMediumScreenOrUp(event.matches);
    };

    setIsMediumScreenOrUp(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleMediaQueryChange);

    return () => {
      mediaQuery.removeEventListener("change", handleMediaQueryChange);
    };
  }, []);

  const apiParams: any = {
    doctorId,
    limit,
    page: currentPage,
    ...(searchValue && { search: searchValue }),
    ...(appType.category !== "All" && { category: appType.category }),
    ...(appType.mode !== "All" && { mode: appType.mode }),
    ...(appointmentStatus !== "All" && {
      appointmentStatus: appointmentStatus,
    }),
    filterType: active, // ✅ Pass 'upcoming' or 'past'
  };

  const { data: appointments, isLoading: appointmentsLoading } =
    useGetAppointments(apiParams);

  const queryKey = [
    "getAppointments",
    doctorId,
    limit,
    currentPage,
    searchValue || undefined,
    appType.category === "All" ? undefined : appType.category,
    appType.mode === "All" ? undefined : appType.mode,
    appointmentStatus === "All" ? undefined : appointmentStatus,
  ];
  const cachedData: any = queryClient.getQueryData(queryKey);

  const allAppointments = useMemo(() => {
    return cachedData?.data?.appointments?.length
      ? cachedData.data.appointments
      : appointments?.data?.appointments ?? [];
  }, [cachedData, appointments]);

  const appsToShow = useMemo(() => {
    if (!allAppointments) return [];
    return allAppointments
      .filter((app: any) =>
        active === "upcoming" ? isFutureDay(app?.date) : !isFutureDay(app?.date)
      )
      .map((app: any) => ({
        ...app,
        appointmentType: {
          category: app?.appointmentType?.category || "Initial",
          mode: app?.appointmentType?.mode || "Online",
        },
        appointmentStatus: app?.appointmentStatus || "Active",
      }));
  }, [active, allAppointments]);

  const totalAppointmentsFromAPI = appointments?.data?.total;
  const showPagination =
    appsToShow.length > 0 && appointments?.data?.lastPage > 1;

  const isTrulyEmpty =
    cachedData?.data?.total === 0 &&
    !appointmentsLoading &&
    !allAppointments.length;

  const scheduleIcon = (
    <Img
      src={add}
      alt="Schedule"
      h={isMediumScreenOrUp ? "16px" : "12px"}
      w={isMediumScreenOrUp ? "16px" : "12px"}
    />
  );

  return (
    <div className="flex flex-col h-full bg-gray-50 p-2 sm:p-4">
      <div className="flex flex-col flex-grow rounded-lg shadow-lg bg-white overflow-hidden">
        {isTrulyEmpty ? (
          <Flex
            flex={1}
            h="full"
            justifyContent="center"
            alignItems="center"
            className="p-4"
          >
            <NoItemsCard
              btnText="Schedule New Appointment"
              handleBtnClick={() => setIsOpen(true)}
              subtitle="You do not have any appointments yet"
              title="No appointments"
            />
          </Flex>
        ) : (
          <>
            <div className="p-4 border-b border-gray-200">
              <AppointmentTabs
                active={active}
                setActive={setActive}
                search={searchValue}
                setSearch={setSearchValue}
                setPage={setCurrentPage}
                searchPlaceholder="Search by patient name "
              />
              <div className="mt-3">
                <AppointmentUtils
                  setPage={setCurrentPage}
                  appType={appType}
                  setAppType={setAppType}
                  appointmentStatus={appointmentStatus}
                  setAppointmentStatus={setAppointmentStatus}
                  px="0"
                />
              </div>
            </div>

            <div className="flex-grow overflow-y-auto p-4">
              {appointmentsLoading &&
              !appsToShow.length &&
              !cachedData?.data?.appointments?.length ? (
                <Flex
                  flex={1}
                  h="full"
                  justifyContent="center"
                  alignItems="center"
                >
                  <p>Loading appointments...</p>
                </Flex>
              ) : !appointmentsLoading && appsToShow.length === 0 ? (
                <Flex
                  flex={1}
                  h="full"
                  justifyContent="center"
                  alignItems="center"
                >
                  <p className="text-gray-500 text-center">
                    No appointments match the current filters in the "{active}"
                    tab.
                  </p>
                </Flex>
              ) : (
                <AppointmentTable
                  setIsCancelModalOpen={setIsCancelModalOpen}
                  setIsDetailsModalOpen={setIsDetailsModalOpen}
                  setIsRescheduleModalOpen={setIsRescheduleModalOpen}
                  appointments={appsToShow}
                  loadingAppointments={
                    appointmentsLoading &&
                    (appsToShow.length > 0 ||
                      !!cachedData?.data?.appointments?.length)
                  }
                  totalAppointments={totalAppointmentsFromAPI}
                  setIsCompleteModalOpen={setIsCompleteModalOpen}
                  setSelectedAppointment={setSelectedAppointment}
                />
              )}
            </div>

            <div
              className={`flex flex-col md:flex-row ${
                showPagination ? "md:justify-between" : "md:justify-end"
              } md:items-baseline items-center gap-4 p-4 border-t border-gray-200`}
            >
              {showPagination && (
                <div className="w-full md:w-auto order-1 md:order-1">
                  <Pagination
                    totalPages={appointments?.data?.lastPage}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
              <div
                className={`order-2 ${
                  showPagination
                    ? "md:order-2 w-full md:w-auto"
                    : "w-full md:w-auto"
                }`}
              >
                <Button
                  text="Schedule New Appointment"
                  icon={scheduleIcon}
                  theme="primary"
                  size="sm"
                  className="rounded-md h-[40px] text-sm w-full md:w-auto"
                  onClick={() => setIsOpen(true)}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <ScheduleAppointmentModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Schedule New Appointment"
      />
      <AppointmentDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        title="Appointment Details"
        appointment={selectedAppointment}
      />
      <RescheduleAppointmentModal
        isOpen={isRescheduleModalOpen}
        onClose={() => setIsRescheduleModalOpen(false)}
        title="Edit/Reschedule/Fix FollowUp Appointment"
        appointment={selectedAppointment}
        address=""
        notes=""
      />

      <CompleteAppointmentModal
        isOpen={isCompleteModalOpen}
        onClose={() => setIsCompleteModalOpen(false)}
        title="Complete Appointment"
        appointment={selectedAppointment}
      />

      <CancelAppointmentModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        title="Cancel Appointment"
        openRescheduleModal={setIsRescheduleModalOpen}
        appointment={selectedAppointment}
      />
    </div>
  );
};

export default Appointment;
